import { DateFilter } from "@shared/utils/formatters/dates"

// Request
// --------------------------------------------------------------------------------------------------------------------

export type Requester<I, O> = (input: I, getToken: () => Promise<string>) => Promise<O>

export type Request<I, O> = (input: I, getToken: () => Promise<string>) => Promise<Result<O>>

// Result
// --------------------------------------------------------------------------------------------------------------------

export type Result<O> = Ok<O> | Err

export type Ok<O> = Readonly<{ tag: "ok"; data: O }>

export type Err = Readonly<{ tag: "err"; error: RequestError; data: undefined }>

// Hook
// --------------------------------------------------------------------------------------------------------------------

export type RequestHook<I, O, SK = []> = (input: I) => [RequestState<O>, Reload<I, SK>]

export type RequestState<O> = Readonly<{ result?: Result<O>; loading: boolean }>

export type Reload<I, SK = []> = (input?: I & AdditionalParams<SK>) => void

// Errors
// --------------------------------------------------------------------------------------------------------------------

export type RequestError = MalformedError | InvalidError | UnknownError | NotFoundError | ForbiddenError | AuthError

export type MalformedError = Readonly<{ type: "MALFORMED"; payload: ReadonlyArray<string> }>

export type InvalidError = Readonly<{ type: "INVALID"; payload: InvalidPayload }>

export type InvalidPayload = Readonly<{ type: string; payload: Record<string, unknown> }>

export type UnknownError = Readonly<{ type: "UNKNOWN"; payload: unknown }>

export type NotFoundError = Readonly<{ type: "NOT_FOUND" }>

export type ForbiddenError = Readonly<{ type: "FORBIDDEN" }>

export type AuthError = Readonly<{ type: "AUTH" }>

export type GraphQLError = Readonly<{ type: "UNKNOWN"; payload: GraphQLPayload }>

type GraphQLPayload = Readonly<{ graphQLErrors: ReadonlyArray<GraphQLErrorProperties> }>

type GraphQLErrorProperties = Readonly<{ extensions: GraphQLErrorExtensionProperties }>

type GraphQLErrorExtensionProperties = Readonly<{ exception: GraphQLErrorExceptionProperties }>

type GraphQLErrorExceptionProperties = Readonly<{ reason: string }>

// Used by FE

export type Data<T> = {
  total: number
  next: string | null
  items: ReadonlyArray<T>
}

export type SortOrder = "ASC" | "DESC"

export const isSortOrder = (value: unknown): value is SortOrder => value === `ASC` || value === `DESC`
export const toSortOrder = (value: string | null, defaultValue: SortOrder = `DESC`): SortOrder =>
  isSortOrder(value) ? value : defaultValue

export type AdditionalParams<SK> = Readonly<{
  after?: string
  term?: string
  sortKey?: SK
  sortOrder?: SortOrder
  limit?: number
  fromDate?: DateFilter | string
  toDate?: string | null
}>
